import React, { useEffect, useState } from "react";
import { Cloud, CloudOff, RefreshCw, AlertTriangle, ChevronRight } from "lucide-react";
import { OfflineGateway } from "@/lib/offline/gateway";
import { connectivity } from "@/lib/offline/connectivity";

// Discreet global offline/sync indicator. Quiet when online with nothing pending.
export default function SyncStatus() {
  const [s, setS] = useState(null);
  const [open, setOpen] = useState(false);
  const [attention, setAttention] = useState([]);
  const [expanded, setExpanded] = useState(null);

  const refresh = async () => {
    try {
      setS(await OfflineGateway.status());
      setAttention(await OfflineGateway.attentionItems());
    } catch (e) { /* noop */ }
  };
  useEffect(() => {
    refresh();
    const unsub = connectivity.subscribe(refresh);
    const t = setInterval(refresh, 5000);
    return () => { unsub(); clearInterval(t); };
  }, []);

  if (!s) return null;
  const offline = s.connectivity === "offline";
  const waiting = (s.pending || 0) + (s.syncing || 0);
  const failed = s.failed || 0;
  if (!offline && waiting === 0 && failed === 0) return null; // silent in normal online use

  let label = "Online";
  let Icon = Cloud;
  let cls = "text-slate-500 bg-white";
  if (offline) { label = "Offline"; Icon = CloudOff; cls = "text-amber-700 bg-amber-50"; }
  else if (s.syncing > 0) { label = "Syncing…"; Icon = RefreshCw; cls = "text-blue-600 bg-blue-50"; }
  else if (failed > 0) { label = "Sync problem"; Icon = AlertTriangle; cls = "text-red-600 bg-red-50"; }
  else if (waiting > 0) { label = `${waiting} waiting`; Icon = Cloud; cls = "text-slate-600 bg-white"; }

  const reason = (a) => {
    if (a.state === "conflict") return "Conflicts with the server";
    if (a.state === "failed_permanent") return a.retryable ? "Kept failing to sync" : "Rejected by the server";
    return "Waiting to retry";
  };

  const doRetry = async (opId) => { await OfflineGateway.retryOperation(opId); refresh(); };
  const doResolve = async (opId) => { await OfflineGateway.resolveOperation(opId); refresh(); };

  return (
    <div className="fixed bottom-4 right-4 z-50" data-testid="sync-status">
      <button onClick={() => setOpen((o) => !o)} className={`flex items-center gap-2 text-xs font-semibold rounded-full shadow border border-slate-200 px-3 py-1.5 ${cls}`} data-testid="sync-status-pill">
        <Icon size={14} className={s.syncing > 0 ? "animate-spin" : ""} /> {label}
      </button>
      {open && (
        <div className="absolute bottom-10 right-0 w-72 bg-white rounded-xl shadow-lg border border-slate-200 p-3 text-xs" data-testid="sync-detail">
          <div className="flex justify-between py-1"><span className="text-slate-500">Waiting</span><b>{waiting}</b></div>
          <div className="flex justify-between py-1"><span className="text-slate-500">Needs attention</span><b>{failed}</b></div>
          <div className="flex justify-between py-1"><span className="text-slate-500">Connection</span><b>{s.connectivity}</b></div>
          <div className="flex justify-between py-1"><span className="text-slate-500">Last sync</span><b>{s.last_sync ? new Date(s.last_sync).toLocaleTimeString() : "—"}</b></div>

          {attention.length > 0 && (
            <div className="mt-3 pt-2 border-t border-slate-100" data-testid="needs-attention">
              <div className="flex items-center gap-1 text-slate-700 font-semibold mb-1">
                <AlertTriangle size={12} className="text-red-500" /> Needs attention
              </div>
              <div className="max-h-48 overflow-y-auto space-y-1.5">
                {attention.map((a) => (
                  <div key={a.operation_id} className="rounded-lg border border-slate-200 p-2" data-testid={`attention-item-${a.operation_id}`}>
                    <button onClick={() => setExpanded((e) => (e === a.operation_id ? null : a.operation_id))} className="w-full flex items-center justify-between text-left" data-testid={`attention-toggle-${a.operation_id}`}>
                      <span className="font-medium text-slate-700">{a.entity_type}</span>
                      <span className="flex items-center gap-1 text-slate-500">{reason(a)}<ChevronRight size={12} className={expanded === a.operation_id ? "rotate-90 transition-transform" : "transition-transform"} /></span>
                    </button>
                    {expanded === a.operation_id && (
                      <div className="mt-1.5 text-[11px] text-slate-500 space-y-0.5" data-testid={`attention-details-${a.operation_id}`}>
                        <div>Action: {a.action}</div>
                        {a.status != null && <div>Server response: {a.status}</div>}
                        {a.reference && <div>Reference: {a.reference}</div>}
                        <div>Recorded: {a.created_at_device ? new Date(a.created_at_device).toLocaleString() : "—"}</div>
                        <div>Attempts: {a.attempt_count}</div>
                      </div>
                    )}
                    <div className="mt-1.5 flex gap-1.5">
                      {a.retryable && (
                        <button onClick={() => doRetry(a.operation_id)} className="flex-1 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-md py-1 font-semibold" data-testid={`attention-retry-${a.operation_id}`}>Retry</button>
                      )}
                      {a.resolvable && (
                        <button onClick={() => doResolve(a.operation_id)} className="flex-1 bg-brand hover:bg-brand-hover text-white rounded-md py-1 font-semibold" data-testid={`attention-resolve-${a.operation_id}`}>Resolve</button>
                      )}
                      {!a.retryable && !a.resolvable && (
                        <span className="flex-1 text-center text-[11px] text-slate-400 py-1" data-testid={`attention-unresolved-${a.operation_id}`}>Kept until resolved</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <button onClick={() => OfflineGateway.sync().then(refresh)} className="mt-2 w-full bg-brand hover:bg-brand-hover text-white rounded-lg py-1.5 font-semibold" data-testid="sync-now">Sync now</button>
        </div>
      )}
    </div>
  );
}
